import vue from 'vue';
import vuex from 'vuex';

import IocContainer from '../container/IocContainer';
import SERVICES from '../services/services';

import User from '../interfaces/User';
import Auth from '../interfaces/Auth';
import Course from '../interfaces/Course';
import Unit from '../interfaces/Unit';

const userProvider = IocContainer.get < User > (SERVICES.USER);
const authProvider = IocContainer.get < Auth > (SERVICES.AUTH);
const courseProvider = IocContainer.get < Course > (SERVICES.COURSE);
const unitProvider = IocContainer.get < Unit > (SERVICES.UNIT);

vue.use(vuex);

const store = new vuex.Store({
  strict: true,

  // Inner Data Structure
  state: {
    loadingUser: false,
    loadingLesson: false,
    auth: false,
    user: {
      id: Number,
      name: String,
      email: String,
      role_id: Number,
      avatar: String,
      last_name: String,
      phone: String,
      gender: String,
      expiration: Date,
      birthday: String,
      address: String,
      city: String,
      country: String,
      state: String,
      zip: Number,
      practicum_fee: Boolean,
      origin: String,
    },
    progress: [],
    course_id: null,
    courseTitle: String,
    course: [],
    lessons: [],
    errors: [],
  },

  // Outside communication with data.
  actions: {
    async loadUser({ commit }) {
      if (authProvider.isAuthenticated()) {
        await userProvider.getUser().then(
          (response) => {
            if (response.data.user) {
              commit('SET_USER', response.data.user);
              commit('SET_AUTH', true);
              setTimeout(() => { commit('SET_LOADING_USER', true) }, 1000);
            }
            //else authProvider.logout()
          },
          (error) => {
            authProvider.logout();
            commit('SET_ERROR', error.response.data.error);
          },
        );
      }
    },
    async loadCourse({ commit, state }) {
      await courseProvider.getCours(state.course_id).then(
        (response) => {
          commit('SET_COURSE', response.data.content);
          commit('SET_COURSE_TITLE', response.data.title);
          commit('RESET_LESSONS');
          response.data.content.forEach((block, b) => {
            block.units.forEach((unit, u) => {
              unitProvider.getUnit(unit.id).then(
                (response) => {
                  response.data.content.forEach((lesson, l) => {
                    // Add Unit nfo
                    lesson.unitId = unit.id;

                    // Add Order information
                    lesson.orderBlock = b;
                    lesson.orderUnit = u;
                    lesson.orderLesson = l;
                    // Calc Order Value

                    lesson.order = b.toString() + u.toString() + l.toString();

                    // Add User progress to Lesson info
                    lesson.pass = (state.progress.indexOf(state.progress.find((l) => {
                      return l.id === lesson.id && l.type === lesson.type && l.reset == 'false';
                    })) > -1)
                    // Check that user already pass Quiz
                    if (lesson.type === 'App\\Models\\Quiz' && lesson.pass) {
                      lesson.result = state.progress.find((l) => {
                        return l.id === lesson.id && l.type === lesson.type;
                      }).result
                    }

                    // Store lesson info to vuex
                    commit('ADD_LESSON', lesson);
                  });
                },
                (error) => {
                  console.error(error.response.data.error);
                  commit('SET_ERROR', error.response.data.error);
                },
              );
            });
          });
        },
        (error) => {
          console.error(error);
        },
      );
    },

    setLoadingUser({ commit }, status) {
      if (status) {
        commit('SET_LOADING_USER', status);
      }
    },

    setLoadingLesson({ commit }, status) {
      if (status) {
        commit('SET_LOADING_LESSON', status);
      }
    },

    setAuth({ commit }, status) {
      if (status) {
        commit('SET_AUTH', status);
      }
    },

    setUser({ commit }, user) {
      if (user) {
        commit('SET_USER', user);
        commit('SET_LOADING_USER', true);
      }
    },

    setCourse({ commit }, course) {
      if (course) {
        commit('SET_COURSE', course);
      }
    },

    saveLessonProgress({ commit }, lesson) {
      commit('SET_LESSON_PROGRESS', lesson);
    },

    saveProgress({ commit }, progress) {
      commit('SET_PROGRESS', progress);
    },
    setExpiration({ commit }, expiration) {
      commit('SET_EXPIRATION_COURSE', expiration);
    },
  },

  // Logic that change data
  mutations: {
    SET_EXPIRATION_COURSE(state, expiration) {
      state.user.expiration = expiration;
    },
    SET_LOADING_USER(state, status) {
      state.loadingUser = status;
    },

    SET_LOADING_LESSON(state, status) {
      state.loadingLesson = status;
    },

    SET_AUTH(state, status) {
      state.auth = status;
    },

    SET_USER(state, user) {
      state.user.id = (user.id) ? user.id : null;
      state.user.name = (user.name) ? user.name : '';
      state.user.email = (user.email) ? user.email : '';
      state.user.role_id = (user.role_id) ? user.role_id : null;
      state.user.avatar = (user.avatar) ? user.avatar : '';
      state.user.last_name = (user.last_name) ? user.last_name : '';
      state.user.phone = (user.phone) ? user.phone : '';
      state.user.gender = (user.gender) ? user.gender : '';
      state.user.birthday = (user.birthday) ? user.birthday : '';
      state.user.address = (user.address) ? user.address : '';
      state.user.city = (user.city) ? user.city : '';
      state.user.country = (user.country) ? user.country : '';
      state.user.state = (user.state) ? user.state : '';
      state.user.zip = (user.zip) ? user.zip : null;
      state.user.expiration = (user.expiration) ? user.expiration : null;
      state.progress = (user.progress_data) ? user.progress_data : [];
      state.course_id = (user.course_id) ? user.course_id : null;
      state.user.practicum_fee = (user.practicum_fee) ? user.practicum_fee : null;
      state.user.origin = (user.origin) ? user.origin : '';
    },

    SET_COURSE(state, course) {
      state.course = course;
    },

    SET_COURSE_TITLE(state, title) {
      state.courseTitle = title;
    },

    SET_LESSON_PROGRESS(state, lesson) {
      const progressIndex = state.progress.indexOf(state.progress.find((progressLesson) => {
        return lesson.id === progressLesson.id && lesson.type === progressLesson.type && !progressLesson.hasOwnProperty('reset');
      }));

      const lessonIndex = state.lessons.indexOf(state.lessons.find((lessonState) => {
        return lesson.id === lessonState.id && lesson.type === lessonState.type;
      }));

      // Set Pass
      if (lesson.type === 'App\\Models\\Lesson') {
        state.lessons[lessonIndex].pass = true;
        lesson.pass = true;
      }

      // if (lesson.type === 'App\\Models\\Quiz') {
      //   if (lesson.hasOwnProperty('result')){
      //     state.lessons[lessonIndex].pass = true;
      //     lesson.pass = true;
      //   }
      // }

      // Check if exist
      if (progressIndex > -1) {
        state.progress[progressIndex] = lesson;
      } else {
        state.progress.push(lesson);
      }

      // Push to server
      userProvider.setUserProgress(JSON.stringify(state.progress));
    },

    SET_PROGRESS(state, progress) {
      state.progress = progress;
      // Push to server
      userProvider.setUserProgress(JSON.stringify(state.progress));
    },
    RESET_LESSONS(state) {
      state.lessons = [];
    },
    ADD_LESSON(state, lesson) {
      state.lessons.push(lesson);
      state.lessons.sort((a, b) => {
        if (a.order.length == b.order.length){
          if (a.order > b.order) {
            return 1;
          }
          if (a.order < b.order) {
            return -1;
          }
          return 0;
        }
        else{
          if (a.order.substr(0, 2) > b.order.substr(0, 2)){
            return 1;
          }
          if (a.order.substr(0, 2) < b.order.substr(0, 2)) {
            return -1;
          }
          return 0;
        }
      });

    // state.lessons.sort(function(a, b) {
    //     return a.order - b.order;
    //   });
    },
  },

  // Get Data
  getters: {
    auth(state) {
      return state.auth;
    },
    user(state) {
      return state.user;
    },
  },

});

export default store;
